import type React from "react";
import { DealFileCard } from "~/components/common/deals/show/files/DealFileCard";
import { LeaseContractFileCard } from "~/components/common/deals/show/files/LeaseContractFileCard";
import { LeasingInspectionFileCard } from "~/components/common/deals/show/files/LeasingInspectionFileCard";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import { useKtLeasings } from "~/hooks/useKtLeasings";
import type { NskResponse } from "~/models/Api";
import type { Deal } from "~/models/Deal";
import type {
	DealUploadFile,
	DealUploadFileUpdateParam
} from "~/models/DealUploadFile";
import {
	getInspectionFilePath,
	getLeaseContractFilePath
} from "~/utils/api/Deal";
import {
	create as postPrivateFile,
	update as putPrivateFile
} from "~/utils/api/deals/DealCompanyPrivateFile";
import { create as uploadMemorandumFile } from "~/utils/api/deals/DealMemorandumFile";
import {
	create as postOtherFile,
	update as putOtherFile
} from "~/utils/api/deals/DealOtherFile";
import {
	getDownloadPath,
	create as postReceiptFile
} from "~/utils/api/deals/DealReceiptFile";
import {
	destroy as destroyFile,
	getFilePath as getClientFilePath
} from "~/utils/api/deals/DealUploadedFile";

function InspectionFileSection({ deal }: { deal: Deal }) {
	const { data: leasing, isLoading } = useKtLeasings().leasingInfo(
		String(deal.leasingId!)
	);
	if (isLoading) return null;
	return (
		<LeasingInspectionFileCard
			deal={deal}
			hasInspectionFile={leasing?.hasInspectionFile ?? false}
			inspectionFilePath={getInspectionFilePath(deal.id)}
		/>
	);
}

export const DealFileListClient: React.FC = () => {
	const id = useIdQueryHandler();
	const { data: deal, mutate: mutateDeal } = useDeals().show(Number(id));
	if (deal === null) {
		return null;
	}
	const handleGetFilePath = (file: DealUploadFile): string =>
		getClientFilePath(deal.id, file);
	const handleMutate = async (): Promise<void> => {
		await mutateDeal?.();
	};
	const handleDestroyFile = async (
		file: DealUploadFile
	): Promise<NskResponse> => destroyFile(deal.id, file.id);
	const handleUploadOther = async (file: File): Promise<NskResponse> =>
		postOtherFile(file, deal.id);
	const handleUpdateOther = async (
		dealOtherParam: DealUploadFileUpdateParam
	): Promise<NskResponse> => putOtherFile(dealOtherParam, deal.id);
	const handleUploadPrivateFile = async (file: File): Promise<NskResponse> =>
		postPrivateFile(file, deal.id);
	const handleUpdatePrivateFile = async (
		params: DealUploadFileUpdateParam
	): Promise<NskResponse> => putPrivateFile(deal.id, params);
	const handleUploadReceipt = async (file: File): Promise<NskResponse> =>
		postReceiptFile(file, deal.id);
	const createPdf = async (): Promise<NskResponse> =>
		postReceiptFile(null, deal.id);
	const downloadExcel = (): void => {
		const path = getDownloadPath(deal.id);
		window.open(path);
	};
	const handleUploadMemorandum = async (file: File): Promise<NskResponse> =>
		uploadMemorandumFile(file, deal.id);
	return (
		<>
			<LeaseContractFileCard
				deal={deal}
				handleGetFile={getLeaseContractFilePath}
				handleMutate={handleMutate}
			/>
			<DealFileCard
				deal={deal}
				files={deal.dealMemorandumFiles}
				fileType="賃借人同意書"
				handleDestroy={handleDestroyFile}
				handleGetFilePath={handleGetFilePath}
				handleMutate={handleMutate}
				handleUpload={handleUploadMemorandum}
			/>
			{deal.leasingId !== null && <InspectionFileSection deal={deal} />}
			<DealFileCard
				createPdf={createPdf}
				deal={deal}
				description="登録されている工事発注書・請書は必要に応じてお手続きにご利用ください"
				downloadExcel={downloadExcel}
				files={deal.dealReceiptFiles}
				fileType="工事発注書・請書"
				handleDestroy={handleDestroyFile}
				handleGetFilePath={handleGetFilePath}
				handleMutate={handleMutate}
				handleUpload={handleUploadReceipt}
			/>
			<DealFileCard
				deal={deal}
				description="施工会社とファイルが共有されます"
				files={deal.dealOtherFiles}
				fileType="その他ファイル（共有用）"
				handleDestroy={handleDestroyFile}
				handleGetFilePath={handleGetFilePath}
				handleMutate={handleMutate}
				handleUpdate={handleUpdateOther}
				handleUpload={handleUploadOther}
			/>
			<DealFileCard
				deal={deal}
				description="施工会社とファイルが共有されません"
				files={deal.dealCompanyPrivateFiles ?? []}
				fileType="その他ファイル（社内用）"
				handleDestroy={handleDestroyFile}
				handleGetFilePath={handleGetFilePath}
				handleMutate={handleMutate}
				handleUpdate={handleUpdatePrivateFile}
				handleUpload={handleUploadPrivateFile}
			/>
		</>
	);
};
